import { createMemo, createSignal, For, Index, onCleanup, onMount, Show } from "solid-js";
import { anneeDecimale, type Chronologie as DonneesChronologie } from "../domaine/analyses";
import type { DateIso, Portefeuille } from "../domaine/types";
import { couleursCibles } from "./analyse";

const HAUTEUR_LIGNE = 17;
const GOUTTIERE = 170;
const HAUT_AXE = 30;
const SANS_DATE_AFFICHES = 8;

/**
 * Une barre par projet, du début à la fin. Les projets sans date ne sont pas
 * masqués : une bande en bas les nomme (ADR-0005 R6 du POC).
 */
export function Chronologie(props: {
  donnees: DonneesChronologie;
  portefeuille: Portefeuille;
  aujourdhui: DateIso;
  surProjet: (ref: string) => void;
  refSvg?: (svg: SVGSVGElement) => void;
}) {
  let conteneur: HTMLDivElement | undefined;
  const [largeurDispo, setLargeurDispo] = createSignal(900);
  onMount(() => {
    if (!conteneur) return;
    const mesurer = () => {
      const l = conteneur?.getBoundingClientRect().width ?? 0;
      if (l > 0) setLargeurDispo(Math.round(l));
    };
    mesurer();
    if (typeof ResizeObserver === "undefined") return;
    const observateur = new ResizeObserver(mesurer);
    observateur.observe(conteneur);
    onCleanup(() => observateur.disconnect());
  });

  const largeur = () => Math.max(560, largeurDispo() - 6);
  const hauteur = () =>
    HAUT_AXE +
    props.donnees.dates.length * HAUTEUR_LIGNE +
    (props.donnees.sansDate.length ? 46 : 0) +
    12;
  const utile = () => largeur() - GOUTTIERE - 16;
  const x = (a: number) =>
    GOUTTIERE + ((a - props.donnees.min) / (props.donnees.max - props.donnees.min)) * utile();
  const annees = createMemo(() => {
    const liste: number[] = [];
    for (let a = props.donnees.min; a <= props.donnees.max; a++) liste.push(a);
    return liste;
  });
  const maintenant = () => anneeDecimale(props.aujourdhui);

  return (
    <div ref={(e) => (conteneur = e)} class="chronologie">
      <svg
        ref={(e) => props.refSvg?.(e)}
        viewBox={`0 0 ${largeur()} ${hauteur()}`}
        width={largeur()}
        height={hauteur()}
        role="img"
        aria-label={`Chronologie : ${props.donnees.dates.length} projets datés, ${props.donnees.sansDate.length} sans date`}
      >
        <For each={annees()}>
          {(a) => (
            <>
              <line
                class="chronologie__graduation"
                x1={x(a)}
                x2={x(a)}
                y1={HAUT_AXE - 6}
                y2={hauteur() - 10}
              />
              <text class="chronologie__annee" x={x(a)} y={HAUT_AXE - 12} text-anchor="middle">
                {a}
              </text>
            </>
          )}
        </For>
        <Show when={maintenant() >= props.donnees.min && maintenant() <= props.donnees.max}>
          <line
            class="chronologie__aujourdhui"
            x1={x(maintenant())}
            x2={x(maintenant())}
            y1={HAUT_AXE - 6}
            y2={hauteur() - 10}
          />
        </Show>
        <Index each={props.donnees.dates}>
          {(d, i) => {
            const y = () => HAUT_AXE + i * HAUTEUR_LIGNE;
            const couleurs = () => couleursCibles(props.portefeuille, d().projet);
            return (
              <g>
                <text
                  class="chronologie__etiquette"
                  x={GOUTTIERE - 10}
                  y={y() + HAUTEUR_LIGNE * 0.72}
                  text-anchor="end"
                  onClick={() => props.surProjet(d().projet.ref)}
                >
                  {d().projet.acronyme}
                </text>
                <rect
                  x={x(d().debut)}
                  y={y() + 2.5}
                  rx={3}
                  width={Math.max(3, x(d().fin) - x(d().debut))}
                  height={HAUTEUR_LIGNE - 5}
                  fill={couleurs()[0] ?? "var(--cible-autre)"}
                  fill-opacity={couleurs().length > 1 ? 0.55 : 0.85}
                >
                  <title>
                    {d().projet.acronyme} · {d().projet.dateDebut?.slice(0, 4) ?? "?"} →{" "}
                    {d().projet.dateFin?.slice(0, 4) ?? "?"}
                    {d().projet.dureeMois ? ` (${d().projet.dureeMois} mois)` : ""}
                  </title>
                </rect>
              </g>
            );
          }}
        </Index>
        <Show when={props.donnees.sansDate.length}>
          <rect
            class="chronologie__bande"
            x={GOUTTIERE}
            y={HAUT_AXE + props.donnees.dates.length * HAUTEUR_LIGNE + 10}
            width={utile()}
            height={26}
            rx={4}
          />
          <text
            class="chronologie__sans-date"
            x={GOUTTIERE + 12}
            y={HAUT_AXE + props.donnees.dates.length * HAUTEUR_LIGNE + 27}
          >
            {props.donnees.sansDate.length} sans date :{" "}
            {props.donnees.sansDate
              .slice(0, SANS_DATE_AFFICHES)
              .map((p) => p.acronyme)
              .join(", ")}
            {props.donnees.sansDate.length > SANS_DATE_AFFICHES ? "…" : ""}
          </text>
        </Show>
      </svg>
    </div>
  );
}
